import { AccountInfo, AuthenticationResult, EventType, PublicClientApplication } from '@azure/msal-browser'

import { buildMsalConfig } from './authConfig'

import { EPublicRoutes } from '@/common/constants/routes/public.routes'
import {
  clearAppInsightsUser,
  identificadorUsuario,
  setAppInsightsUser,
} from '@/core/providers/ConfigAppInsightsContainer/AppInsights'
import { reportarAdvertencia } from '@/core/telemetria/reportarError'

// La instancia se crea de forma DIFERIDA (lazy), recién en el primer acceso en runtime,
// que ocurre DESPUÉS de que ConfigRuntimeProvider hidrató el config (CLIENT_ID/API_SCOPE
// vienen de /api/config). Construirla en import time daría clientId vacío.
//
// Se persiste en globalThis (NO en un `let` de módulo): en dev, Fast Refresh/HMR re-evalúa
// este módulo y resetearía un `let`, creando una SEGUNDA PublicClientApplication con el mismo
// clientId. Dos instancias inicializadas disparan el warning MSAL "already an instance ...
// with the same client id" y compiten por el mismo cache de localStorage (races en
// handleRedirectPromise / activeAccount). globalThis sobrevive HMR → una única instancia.
declare global {
   
  var __msalInstance: PublicClientApplication | undefined
   
  var __msalInitPromise: Promise<void> | undefined
}

export const getMsalInstance = (): PublicClientApplication => {
  const instance = (globalThis.__msalInstance ??= new PublicClientApplication(buildMsalConfig()))

  return instance
}

// Backward-compat: los call sites existentes siguen usando `msalInstance.metodo()` sin cambios.
// El Proxy reenvía cada acceso a la instancia real (creándola en el primer uso, ya hidratado).
// MsalProvider recibe la instancia REAL vía getMsalInstance() (no el Proxy) en MsalCustomProvider.
export const msalInstance: PublicClientApplication = new Proxy(
  {} as PublicClientApplication,
  {
    get(_target, prop) {
      const instance = getMsalInstance()
      const value = Reflect.get(instance, prop, instance)

      return typeof value === 'function' ? value.bind(instance) : value
    },
  },
)

const doInitialize = async (): Promise<void> => {
  const instance = getMsalInstance()

  // initialize MSAL instance
  await instance.initialize()

  // Account selection logic is app dependent.
  // Adjust as needed for different use cases.
  const accounts = instance.getAllAccounts()

  if (accounts.length > 0) {
    instance.setActiveAccount(accounts[0])
    // Correlacionar la telemetría de Application Insights con el usuario
    setAppInsightsUser(identificadorUsuario(accounts[0]))
  }

  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      // En msal-browser v5 el payload de LOGIN_SUCCESS es AccountInfo
      const account = event.payload as AccountInfo

      instance.setActiveAccount(account)
      setAppInsightsUser(identificadorUsuario(account))
    }
  })

  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      clearAppInsightsUser()
    }
  })

  // La respuesta de un loginRedirect se procesa en la página donde cae, SIN navegar.
  //
  // msal-browser guarda la promesa de la PRIMERA llamada a handleRedirectPromise y se la entrega a
  // todas las siguientes (MsalProvider, useLogin), así que las opciones de esta llamada valen para
  // toda la app. MsalProvider llama sin opciones, y por defecto (navigateToLoginRequestUrl: true),
  // si la respuesta aparece en una página distinta de donde empezó el login, MSAL la vuelve a
  // guardar y navega a esa página. Esa página es /autenticacion/ingreso, y si la cookie ya es
  // válida (otra pestaña entró mientras tanto) el proxy la rebota a HOME: HOME vuelve a navegar al
  // login, el proxy vuelve a rebotar, y la pestaña se recarga sin fin hasta cerrarla (28-sep-2026:
  // ~520 vueltas en 11 minutos). MsalCustomProvider no monta MsalProvider hasta que esto corrió.
  //
  // No se espera: basta con que esta sea la primera llamada. El error lo reciben y manejan los
  // demás que esperan la misma promesa (useLogin avisa, MsalProvider emite LOGIN_FAILURE); el
  // catch de acá solo evita el "unhandled rejection".
  instance.handleRedirectPromise({ navigateToLoginRequestUrl: false }).catch(() => undefined)
}

// initPromise también vive en globalThis: garantiza que doInitialize() (y sus addEventCallback)
// se ejecute UNA sola vez aunque el módulo se re-evalúe por HMR, evitando handlers duplicados
// de LOGIN_SUCCESS/LOGOUT (doble setActiveAccount / doble telemetría).
export const initializeMsal = (): Promise<void> => {
  globalThis.__msalInitPromise ??= doInitialize()

  return globalThis.__msalInitPromise
}

export type TAuthCookie = 'access_token' | 'username'

// Incluye 'roles_list', que ya no se escribe: el proxy toma los roles del token firmado.
// Se sigue borrando para purgar la cookie que quedó de sesiones anteriores.
export const AUTH_COOKIES = ['access_token', 'roles_list', 'username']

// path '/' es OBLIGATORIO también al borrar: el navegador identifica una cookie por
// (nombre, dominio, path). Un removeCookie sin path borra la del path ACTUAL — es decir,
// no borra nada — y la cookie de '/' sobrevive al logout.
const COOKIE_OPTIONS = {
  path: '/',
  secure: true,
  sameSite: 'Lax',
}

/**
 * Guarda la sesión para el proxy. Devuelve false si el ID token ya venció.
 *
 * Ese caso es real: al renovar SOLO el access token de la API (`.default`), MSAL puede
 * devolver el ID token viejo del cache. Escribirlo igual arma un bucle invisible — el proxy
 * lo rechaza y manda a login, y el login lo vuelve a escribir — que deja la pantalla
 * "Verificando sesión…" para siempre y solo se salía borrando los datos del sitio.
 */
export const storeLoginInfo = (
  loginResponse: AuthenticationResult,
  setCookie: (name: TAuthCookie, value: string, options?: object) => void,
): boolean => {
  // idTokenClaims viene tipado como `object` en msal-browser v5
  const exp = (loginResponse.idTokenClaims as { exp?: number } | undefined)?.exp

  if (typeof exp !== 'number' || exp * 1000 <= Date.now()) {
    reportarAdvertencia('El ID token del cache ya venció: hay que autenticarse de nuevo')

    return false
  }

  // ID token (no el access token de Graph): el proxy lo valida LOCALMENTE por firma
  // (aud = CLIENT_ID). Los access tokens de Graph no son validables por terceros
  setCookie('access_token', loginResponse.idToken, COOKIE_OPTIONS)

  // Ya NO se escribe 'roles_list': el proxy toma los roles del propio token (firmado).
  // Username
  setCookie('username', loginResponse.account?.username, COOKIE_OPTIONS)

  return true
}

/**
 * Borra las cookies de sesión. El path '/' es OBLIGATORIO: el navegador identifica una
 * cookie por (nombre, dominio, path), y sin él se intentaría borrar una del path actual
 * — que no existe — dejando viva la de '/'.
 *
 * Se usa document.cookie y no react-cookie para poder llamarla desde código no-React
 * (authHeader, interceptores), que es justo donde se detecta la sesión perdida.
 */
export const clearAuthCookies = (): void => {
  AUTH_COOKIES.forEach((name) => {
    document.cookie = `${name}=; path=/; max-age=0`
  })
}

let redirigiendoALogin = false

/**
 * Sesión caducada o perdida: a la pantalla de login, conservando la ruta actual como
 * returnUrl — en vez de dejar la app colgada "cargando" con llamadas que nunca resolverán.
 *
 * Borrar las cookies acá NO es opcional: son el espejo de la sesión para el proxy. Si MSAL
 * ya no tiene cuenta pero la cookie sigue viva, el proxy considera autenticado al usuario y
 * rebota la pantalla de login de vuelta a HOME → HOME manda a login → bucle infinito.
 */
export const redirectToLoginPreservingRoute = (): void => {
  if (typeof window === 'undefined' || redirigiendoALogin) {
    return
  }
  if (window.location.pathname.startsWith('/autenticacion')) {
    return
  }
  redirigiendoALogin = true
  clearAuthCookies()
  const returnUrl = window.location.pathname + window.location.search

  window.location.assign(`${EPublicRoutes.AUTENTICACION}?returnUrl=${encodeURIComponent(returnUrl)}`)
}
